import { action, computed, makeObservable, observable } from 'mobx';
import { CallService } from '../services/callService';

/**
 * Whether a call can be placed to someone, and why not when it cannot.
 *
 * Three states rather than a boolean because the two reasons want different words: someone with
 * no LoanChat desktop app running cannot be rung at all, and someone already on a call can be
 * rung in a minute. Saying "unavailable" to both lost that, and saying nothing — which is what
 * the chat window did — let people press Huddle at someone who could not possibly answer.
 */
export type CallPresence = 'available' | 'on-another-call' | 'unavailable';

export const CALL_PRESENCE_LABEL: Record<CallPresence, string> = {
  available: 'Available',
  'on-another-call': 'On another call',
  unavailable: 'Unavailable',
};

/**
 * The one rule, from the two facts it is made of.
 *
 * `callable` is vendor-service's: a websocket session on a client where a ring would actually be
 * raised, which today means the desktop app. `busy` is LiveKit's, through los-video-service.
 * Mobile composes the same two in the same order (CallStore.decorateWithPresence), and Meet's
 * directory does too (realMeetDataSource.resolvePresence) — the three were written separately
 * and must keep agreeing, so change them together.
 *
 * Callable is checked first on purpose. Someone signed out of the desktop app but still showing
 * in a room — a stale busy entry, or a call taken on their phone — is not reachable here either
 * way, and "Unavailable" is the more useful of the two true things to say.
 */
export const callPresenceOf = (
  callable: boolean,
  busy: boolean,
): CallPresence => {
  if (!callable) return 'unavailable';
  return busy ? 'on-another-call' : 'available';
};

/** A cached answer older than this is not worth trusting; matches the mobile app's window. */
const BUSY_TTL_MS = 15_000;

/**
 * Who is on a call, for the chat window.
 *
 * Meet keeps its own copy of this inside platform-ui, and the two do not share a process — Meet
 * runs in a BrowserWindow of its own. This is the chat side's, which is what the conversation
 * participant list and the profile card read before offering a Huddle.
 *
 * Polled rather than pushed: nothing broadcasts entering or leaving a LiveKit room, so this is
 * point-in-time like every other presence read in Meet. Refreshed on demand with a short TTL,
 * so a dialog left open does not sit on an answer from ten minutes ago.
 */
export class MeetPresenceStore {
  /** Keyed by user id. Absent means not busy, which is also what a failed load leaves behind. */
  busyUserIds: Record<number, boolean> = {};

  private callService: CallService;
  private loadedAt = 0;
  private inFlight: Promise<void> = null;

  constructor() {
    this.callService = new CallService();
    makeObservable(this, {
      busyUserIds: observable,
      busyCount: computed,
      setBusyUserIds: action,
    });
  }

  get busyCount() {
    return Object.keys(this.busyUserIds).length;
  }

  setBusyUserIds(userIds: number[]) {
    const next: Record<number, boolean> = {};
    userIds.forEach((id) => {
      next[id] = true;
    });
    this.busyUserIds = next;
  }

  isUserBusy(userId: number): boolean {
    return this.busyUserIds[userId] ?? false;
  }

  /**
   * Refresh if the last answer has aged out. Safe to call from a render effect: a second caller
   * while one is in flight joins it rather than starting another.
   */
  load(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this.loadedAt && Date.now() - this.loadedAt < BUSY_TTL_MS) {
      return Promise.resolve();
    }
    this.inFlight = this.callService
      .busyIdentities()
      .then((response) => {
        const identities: string[] = response?.data?.data ?? [];
        this.setBusyUserIds(identities.map(userIdOfIdentity).filter(Boolean));
        this.loadedAt = Date.now();
      })
      .catch((e) => {
        /*
         * Presence failed, not the roster. Everyone stays as they were rather than becoming
         * uncallable, which is the safe direction: the worst case is a call that ends at once
         * with "On another call", and the callee's own client is what guarantees that.
         */
        console.error('Could not read who is on a call', e);
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }
}

/** `user-1234` back to 1234. Anything else is not an identity this app minted. */
const userIdOfIdentity = (identity: string): number | undefined => {
  const id = Number(String(identity ?? '').replace(/^user-/, ''));
  return Number.isFinite(id) && id > 0 ? id : undefined;
};
